import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../layouts/AdminLayout';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { getPendingGrading, gradeAnswer, getExam } from '../../api/admin.api';

const ManualGrading = () => {
  const { id: examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scores, setScores] = useState({});
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([getExam(examId), getPendingGrading(examId)])
      .then(([eRes, gRes]) => {
        setExam(eRes.data.data);
        setAnswers(gRes.data.data);
        const initial = {};
        gRes.data.data.forEach((a) => {
          if (a.manual_score !== null) initial[a.answer_id] = a.manual_score;
        });
        setScores(initial);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleGrade = async (answerId, maxMarks) => {
    const score = parseFloat(scores[answerId]);
    if (isNaN(score) || score < 0 || score > maxMarks) {
      setError(`Score must be between 0 and ${maxMarks}`);
      return;
    }
    setError('');
    setSaving((s) => ({ ...s, [answerId]: true }));
    try {
      await gradeAnswer(answerId, score);
      setSaved((s) => ({ ...s, [answerId]: true }));
      setTimeout(() => setSaved((s) => ({ ...s, [answerId]: false })), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving((s) => ({ ...s, [answerId]: false }));
    }
  };

  if (loading) return <AdminLayout><Spinner className="py-20" /></AdminLayout>;

  return (
    <AdminLayout>
      <div className="mb-7">
        <div className="flex items-center gap-2 mb-2">
          <button onClick={() => navigate(`/admin/exams/${examId}`)}
            className="text-slate-400 hover:text-slate-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-slate-400 text-sm">{exam?.title}</span>
        </div>
        <h1 className="text-2xl font-bold text-slate-900">Manual Grading</h1>
        <p className="text-slate-500 text-sm mt-0.5">Grade short-answer responses below.</p>
      </div>

      {error && <Alert type="error" className="mb-5">{error}</Alert>}

      {answers.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
          <p className="text-slate-400">No short-answer responses pending grading.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {answers.map((a) => (
            <div key={a.answer_id} className="bg-white rounded-2xl border border-slate-200 shadow-card p-5">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  <p className="text-xs text-slate-400 mb-1">{a.candidate_name || a.candidate_email}</p>
                  <p className="text-sm font-medium text-slate-800">{a.question_text}</p>
                </div>
                <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded-lg whitespace-nowrap flex-shrink-0">
                  Max {a.marks} mark(s)
                </span>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 mb-4 border border-slate-100">
                <p className="text-xs text-slate-400 mb-1 font-medium uppercase tracking-wide">Candidate's Answer</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{a.text_answer}</p>
              </div>

              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-slate-700 whitespace-nowrap">Score:</label>
                <input
                  type="number"
                  min="0"
                  max={a.marks}
                  step="0.5"
                  value={scores[a.answer_id] ?? ''}
                  onChange={(e) => setScores((s) => ({ ...s, [a.answer_id]: e.target.value }))}
                  className="w-24 border border-slate-200 rounded-xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-400"
                  placeholder="0"
                />
                <span className="text-xs text-slate-400">/ {a.marks}</span>
                <button
                  onClick={() => handleGrade(a.answer_id, a.marks)}
                  disabled={saving[a.answer_id]}
                  className="px-4 py-1.5 bg-primary-600 text-white text-xs font-semibold rounded-xl hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {saving[a.answer_id] ? 'Saving…' : saved[a.answer_id] ? 'Saved ✓' : 'Save Grade'}
                </button>
                {a.graded_at && !saved[a.answer_id] && (
                  <span className="text-xs text-emerald-600">Previously graded: {a.manual_score}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
};

export default ManualGrading;
